import React from "react";
import { Card, CardBody } from "reactstrap";
import { Icon } from "./dashboardRegistry";

const number = (value) => Number(value || 0);

const percent = (value, total) =>
  total ? `${Math.round((number(value) / number(total)) * 100)}%` : "0%";

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "Not scheduled";

const dueText = (value) => {
  if (!value) return "Not scheduled";
  const days = Math.ceil((new Date(value).getTime() - Date.now()) / 86400000);
  if (days < 0)
    return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  if (days === 0) return "Due today";
  return `Due in ${days} day${days === 1 ? "" : "s"}`;
};

const statusTone = (value) =>
  /complete|approved/i.test(value)
    ? "green"
    : /overdue/i.test(value)
    ? "red"
    : /progress|review|approval/i.test(value)
    ? "blue"
    : "slate";
const empty = (
  <p className="dashboard-widget-placeholder">No data is available.</p>
);

const Panel = ({ title, action, children, className = "" }) => (
  <Card className={`ops-panel ${className}`}>
    <CardBody>
      <div className="ops-panel__header">
        <h2>{title}</h2>
        {action && (
          <button type="button" className="ops-text-action">
            {action}
          </button>
        )}
      </div>
      {children}
    </CardBody>
  </Card>
);

const Donut = ({ value, label, variant = "risk" }) => (
  <div className={`design-donut design-donut--${variant}`}>
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  </div>
);

const Legend = ({ rows, total }) => (
  <div className="design-legend">
    {rows.map((row, index) => (
      <div key={row.label}>
        <i
          className={`is-${
            ["green", "blue", "red", "slate", "amber", "purple"][index % 6]
          }`}
        />
        <span>{row.label}</span>
        <b>
          {row.value} ({percent(row.value, total)})
        </b>
      </div>
    ))}
  </div>
);

const kpiDefinitions = {
  reviews: [
    ["Total Reviews", "total", "bx-file", "blue"],
    ["Completed", "completed", "bx-check-square", "green"],
    ["In Progress", "inProgress", "bx-time-five", "blue"],
    ["Overdue", "overdue", "bx-error", "red"],
    ["Not Started", "notStarted", "bx-time", "slate"],
  ],
  work: [
    ["My Assigned Reviews", "assigned", "bx-task", "blue"],
    ["Completed by Me", "completed", "bx-check-circle", "green"],
    ["In Progress", "inProgress", "bx-time-five", "blue"],
    ["Overdue", "overdue", "bx-error", "red"],
    ["Due This Week", "dueThisWeek", "bx-calendar", "purple"],
  ],
};

const DashboardKpis = ({ widget, data }) => {
  const rows =
    widget.type === "work-kpis" ? kpiDefinitions.work : kpiDefinitions.reviews;
  return (
    <div className="design-kpis">
      {rows.map(([label, key, icon, tone]) => (
        <article key={key}>
          <i className={`is-${tone}`}>
            <Icon name={icon} />
          </i>
          <div>
            <span>{label}</span>
            <strong>{number(data?.[key])}</strong>
          </div>
        </article>
      ))}
    </div>
  );
};

const StatusChart = ({ data, title, label, variant }) => {
  const rows = data?.statuses || [];
  const total = rows.reduce((sum, row) => sum + number(row.value), 0);
  return (
    <Panel title={title}>
      {rows.length ? (
        <div className="design-chart-pair">
          <Donut value={total} label={label} variant={variant} />
          <Legend rows={rows} total={total} />
        </div>
      ) : (
        empty
      )}
    </Panel>
  );
};

const StackedRows = ({ rows = [] }) => (
  <div className="design-bars design-bars--stacked">
    {rows.map((row) => {
      const segments = [
        [row.completed, "green"],
        [row.inProgress, "blue"],
        [row.overdue, "red"],
        [row.notStarted, "slate"],
      ].filter(([value]) => number(value));
      const segmentTotal =
        segments.reduce((sum, [value]) => sum + number(value), 0) ||
        number(row.value) ||
        1;
      return (
        <div key={row.label}>
          <span>{row.label}</span>
          <b>{number(row.value)}</b>
          <em>
            {segments.map(([value, tone]) => (
              <i
                key={tone}
                className={`is-${tone}`}
                style={{ width: percent(value, segmentTotal) }}
              />
            ))}
          </em>
        </div>
      );
    })}
  </div>
);

const ReviewTypes = ({ data }) => (
  <Panel title="Reviews by Review Type">
    <StackedRows rows={data?.byType} />
  </Panel>
);

const Reviewers = ({ data }) => (
  <Panel title="Reviews by Reviewer" action="View All">
    <StackedRows rows={data?.byReviewer} />
  </Panel>
);

const Trend = ({ data }) => {
  const rows = data?.trend || [];
  const max = Math.max(
    1,
    ...rows.flatMap((row) =>
      [row.completed, row.inProgress, row.overdue, row.notStarted].map(number)
    )
  );

  const points = (key) =>
    rows
      .map(
        (row, index) =>
          `${rows.length === 1 ? 250 : (index * 500) / (rows.length - 1)},${
            130 - (number(row[key]) / max) * 110
          }`
      )
      .join(" ");
  return (
    <Panel title="Review Progress Trend" action="Last 6 Months">
      {rows.length ? (
        <div className="design-line-chart is-review">
          <div className="design-line-chart__body">
            <div className="design-line-chart__plot">
              {[
                ["completed", "green"],
                ["inProgress", "blue"],
                ["overdue", "red"],
                ["notStarted", "slate"],
              ].map(([key, tone]) => (
                <svg
                  key={key}
                  viewBox="0 0 500 130"
                  preserveAspectRatio="none"
                  className={`is-${tone}`}
                >
                  <polyline points={points(key)} />
                </svg>
              ))}
            </div>
          </div>
          <div className="design-line-chart__months">
            {rows.map((row) => (
              <span key={row.label}>{row.label}</span>
            ))}
          </div>
          <div className="design-mini-legend">
            <span className="is-green">Completed</span>
            <span className="is-blue">In Progress</span>
            <span className="is-red">Overdue</span>
            <span className="is-slate">Not Started</span>
          </div>
        </div>
      ) : (
        empty
      )}
    </Panel>
  );
};

const DeadlineList = ({ data, title = "Upcoming Deadlines" }) => {
  const rows = data?.deadlines || [];
  return (
    <Panel title={title} action="View Calendar">
      <div className="design-deadline-list">
        {rows.map((row) => (
          <article key={row.id}>
            <time>
              <small>
                {new Date(row.dueDate)
                  .toLocaleDateString("en-US", { month: "short" })
                  .toUpperCase()}
              </small>
              <b>{new Date(row.dueDate).getDate()}</b>
            </time>
            <div>
              <strong>{row.number}</strong>
              <span>
                {row.name} – {row.client}
              </span>
            </div>
            <em
              className={`is-${
                new Date(row.dueDate) < new Date() ? "red" : "blue"
              }`}
            >
              {dueText(row.dueDate)}
            </em>
          </article>
        ))}
      </div>
      {!rows.length && empty}
    </Panel>
  );
};

const Activity = ({ data, title = "Recent Review Activity" }) => (
  <Panel title={title} action="View All">
    <div className="design-activity">
      {(data?.activities || []).map((row, index) => (
        <article key={`${row.date}-${index}`}>
          <i className="is-blue">
            <Icon name="bx-history" />
          </i>
          <div>
            <strong>{row.title}</strong>
            <span>{row.person || "System"}</span>
          </div>
          <time>{row.time}</time>
        </article>
      ))}
    </div>
    {!data?.activities?.length && empty}
  </Panel>
);

const ReviewFilters = ({ data }) => (
  <div className="design-review-filters">
    <button type="button">
      All Inspection Cycles <Icon name="bx-chevron-down" />
    </button>
    <button type="button">
      All Review Types ({data?.byType?.length || 0}){" "}
      <Icon name="bx-chevron-down" />
    </button>
    <button type="button">
      All Reviewers ({data?.byReviewer?.length || 0}){" "}
      <Icon name="bx-chevron-down" />
    </button>
    <button type="button">
      All Statuses ({data?.statuses?.length || 0}){" "}
      <Icon name="bx-chevron-down" />
    </button>
  </div>
);

const AssignedReviews = ({ data }) => {
  const rows = data?.reviews || [];
  return (
    <Panel
      title="My Assigned Reviews"
      action="View All"
      className="design-assigned"
    >
      <div className="design-tabs">
        <button className="is-active" type="button">
          All ({rows.length})
        </button>
        <button type="button">Overdue ({number(data?.overdue)})</button>
        <button type="button">In Progress ({number(data?.inProgress)})</button>
        <button type="button">Completed ({number(data?.completed)})</button>
      </div>
      <div className="design-table">
        <div className="design-table__head">
          <span>Engagement ID</span>
          <span>Engagement Name</span>
          <span>Client</span>
          <span>Review Type</span>
          <span>My Role</span>
          <span>Due Date</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {rows.map((row) => (
          <div key={row.id}>
            <span className="is-link">{row.number}</span>
            <span>{row.name}</span>
            <span>{row.client}</span>
            <span>{row.reviewType}</span>
            <span>{row.role}</span>
            <span className="is-date">{dateText(row.dueDate)}</span>
            <span
              className={`is-status is-${String(row.status)
                .toLowerCase()
                .replace(/[^a-z]/g, "")}`}
            >
              {row.status}
            </span>
            <span>•••</span>
          </div>
        ))}
      </div>
      {!rows.length && empty}
    </Panel>
  );
};

const MyCalendar = ({ data }) => (
  <Panel title="My Calendar" action="View Full Calendar">
    <div className="design-calendar">
      <h3>
        {new Date().toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })}
      </h3>
      {(data?.deadlines || []).slice(0, 5).map((row) => (
        <article key={row.id}>
          <time>
            <b>{new Date(row.dueDate).getDate()}</b>
            <small>
              {new Date(row.dueDate)
                .toLocaleDateString("en-US", { month: "short" })
                .toUpperCase()}
            </small>
          </time>
          <i className={`is-${statusTone(row.status)}`} />
          <div>
            <strong>{row.name}</strong>
            <span>{row.client}</span>
          </div>
          <em>Review due</em>
        </article>
      ))}
    </div>
    {!data?.deadlines?.length && empty}
  </Panel>
);

const MyProgress = ({ data }) => {
  const total = number(data?.assigned);
  const completed = number(data?.completed);
  const onTime = (data?.reviews || []).filter(
    (row) =>
      row.completedOn &&
      (!row.dueDate || new Date(row.completedOn) <= new Date(row.dueDate))
  ).length;
  const rows = [
    [
      "Reviews Completed",
      percent(completed, total),
      `${completed} / ${total}`,
      "green",
    ],
    [
      "Average Completion",
      `${number(data?.averageCompletionDays).toFixed(1)} days`,
      "Assigned to completed",
      "blue",
    ],
    [
      "On-Time Completion",
      percent(onTime, completed),
      `${onTime} / ${completed}`,
      "purple",
    ],
  ];
  return (
    <Panel title="My Progress">
      <div className="design-progress">
        {rows.map(([label, value, detail, tone]) => (
          <article key={label}>
            <i className={`is-${tone}`}>
              <Icon name="bx-award" />
            </i>
            <div>
              <strong>{label}</strong>
              <em>
                <span
                  className={`is-${tone}`}
                  style={{ width: value.includes("%") ? value : "0%" }}
                />
              </em>
            </div>
            <b>
              {value}
              <small>{detail}</small>
            </b>
          </article>
        ))}
      </div>
    </Panel>
  );
};

const DashboardQuote = () => (
  <div className="design-quote">
    <Icon name="bx-file" />
    <q>Consistent quality today builds stronger firms tomorrow.</q>
    <strong>
      PIERS<small>People. Process. Quality.</small>
    </strong>
  </div>
);

const EngagementWidget = ({ data }) => (
  <Panel title="Engagement Overview">
    <div className="design-chart-pair">
      <Donut value={number(data?.total)} label="Total" />
      <Legend rows={data?.statuses || []} total={number(data?.total)} />
    </div>
  </Panel>
);

const definition = (type, displayName, category, icon, columns, Component) => ({
  type,
  displayName,
  category,
  icon,
  supportedSizes: { large: columns },
  defaultSize: "large",
  defaultConfiguration: {},
  Component,
  description: displayName,
  configurable: false,
  removable: true,
});
export const designRegistry = {
  "engagement-kpis": definition(
    "engagement-kpis",
    "Engagement KPIs",
    "KPIs",
    "bx-bar-chart",
    12,
    EngagementWidget
  ),
  "engagement-progress-design": definition(
    "engagement-progress-design",
    "Engagements Progress",
    "Engagement & Inspection",
    "bx-git-branch",
    5,
    EngagementWidget
  ),
  "engagement-risk-design": definition(
    "engagement-risk-design",
    "Engagements by Risk Rating",
    "Risk & Findings",
    "bx-doughnut-chart",
    4,
    EngagementWidget
  ),
  "engagement-cycle-design": definition(
    "engagement-cycle-design",
    "Engagements by Inspection Cycle",
    "Engagement & Inspection",
    "bx-bar-chart",
    3,
    EngagementWidget
  ),
  "engagement-trend-design": definition(
    "engagement-trend-design",
    "Status Trend",
    "Engagement & Inspection",
    "bx-line-chart",
    5,
    EngagementWidget
  ),
  "engagement-client-design": definition(
    "engagement-client-design",
    "Engagements by Client",
    "Engagement & Inspection",
    "bx-buildings",
    3,
    EngagementWidget
  ),
  "key-deadlines-design": definition(
    "key-deadlines-design",
    "Key Deadlines",
    "Schedule",
    "bx-calendar",
    4,
    DeadlineList
  ),
  "engagement-review-type-design": definition(
    "engagement-review-type-design",
    "Engagements by Review Type",
    "Engagement & Inspection",
    "bx-pie-chart",
    4,
    EngagementWidget
  ),
  "open-queries-design": definition(
    "open-queries-design",
    "Open Queries",
    "Queries",
    "bx-message-dots",
    4,
    EngagementWidget
  ),
  "recent-activity-design": definition(
    "recent-activity-design",
    "Recent Activity",
    "Schedule",
    "bx-history",
    4,
    Activity
  ),
  "review-filters": definition(
    "review-filters",
    "Review Filters",
    "Schedule",
    "bx-filter",
    12,
    ReviewFilters
  ),
  "reviews-kpis": definition(
    "reviews-kpis",
    "Review KPIs",
    "KPIs",
    "bx-check-shield",
    12,
    DashboardKpis
  ),
  "review-status-design": definition(
    "review-status-design",
    "Reviews by Status",
    "Workload",
    "bx-doughnut-chart",
    4,
    (props) => (
      <StatusChart
        {...props}
        title="Reviews by Status"
        label="Total"
        variant="reviews"
      />
    )
  ),
  "review-types-design": definition(
    "review-types-design",
    "Reviews by Review Type",
    "Workload",
    "bx-bar-chart",
    4,
    ReviewTypes
  ),
  "reviewers-design": definition(
    "reviewers-design",
    "Reviews by Reviewer",
    "Workload",
    "bx-group",
    4,
    Reviewers
  ),
  "review-trend-design": definition(
    "review-trend-design",
    "Review Progress Trend",
    "Workload",
    "bx-line-chart",
    5,
    Trend
  ),
  "review-deadlines-design": definition(
    "review-deadlines-design",
    "Upcoming Review Deadlines",
    "Schedule",
    "bx-calendar-check",
    4,
    (props) => <DeadlineList {...props} title="Upcoming Review Deadlines" />
  ),
  "review-activity-design": definition(
    "review-activity-design",
    "Recent Review Activity",
    "Schedule",
    "bx-history",
    3,
    Activity
  ),
  "work-kpis": definition(
    "work-kpis",
    "My Work KPIs",
    "KPIs",
    "bx-user-check",
    12,
    DashboardKpis
  ),
  "assigned-reviews-design": definition(
    "assigned-reviews-design",
    "My Assigned Reviews",
    "Workload",
    "bx-table",
    8,
    AssignedReviews
  ),
  "my-calendar-design": definition(
    "my-calendar-design",
    "My Calendar",
    "Schedule",
    "bx-calendar",
    4,
    MyCalendar
  ),
  "work-status-design": definition(
    "work-status-design",
    "My Work by Status",
    "Workload",
    "bx-doughnut-chart",
    4,
    (props) => (
      <StatusChart
        {...props}
        title="My Work by Status"
        label="Assigned"
        variant="work"
      />
    )
  ),
  "work-deadlines-design": definition(
    "work-deadlines-design",
    "Upcoming Deadlines",
    "Schedule",
    "bx-calendar-check",
    4,
    DeadlineList
  ),
  "my-progress-design": definition(
    "my-progress-design",
    "My Progress",
    "Workload",
    "bx-line-chart",
    4,
    MyProgress
  ),
  "dashboard-quote": definition(
    "dashboard-quote",
    "PIERS Message",
    "Schedule",
    "bx-message-rounded-quote",
    12,
    DashboardQuote
  ),
};
